import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { ActivitySourceSchema } from '../activity/activities.schemas';
import { ProspectViewingAttendanceSchema, ProspectViewingImpressionSchema } from '../deal/prospect-viewings.schemas';
import {
  PROSPECT_PHASE_DIRECTIONS,
  ProspectOfferKindSchema,
  ProspectOfferStatusSchema,
  ProspectLossReasonSchema,
  ProspectPhaseReasonCodeSchema,
  ProspectPhaseSchema,
  ProspectSourceSchema,
  ProspectStatusSchema,
} from '../deal/prospects.schemas';
import { InquirySourceSchema } from '../deal/inquiries.schemas';

export const ProspectCreatedPayloadSchema = z.object({
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  phase: ProspectPhaseSchema,
  source: ProspectSourceSchema.nullable(),
  historyId: z.uuid(),
  actor: ActorSchema,
  origin: ActivitySourceSchema.default('APP'),
  createdVia: InquirySourceSchema.nullable().default(null),
  occurredAt: z.iso.datetime(),
});

export type ProspectCreatedPayload = z.infer<typeof ProspectCreatedPayloadSchema>;

export const PROSPECT_TRIGGERS = ['PROSPECT_PAGE'] as const;
export type ProspectTrigger = (typeof PROSPECT_TRIGGERS)[number];

export const ProspectPhaseChangedPayloadSchema = z.object({
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  historyId: z.uuid(),
  fromPhase: ProspectPhaseSchema,
  toPhase: ProspectPhaseSchema,
  direction: z.enum(PROSPECT_PHASE_DIRECTIONS),
  reason: z.string().nullable(),
  reasonCode: ProspectPhaseReasonCodeSchema.nullable().default(null),
  offeredPrice: z.number().nullable(),
  purchasePrice: z.number().nullable(),
  // The Notartermin date this move stored; null when it stored none. Defaulted for outbox rows written before IMM-1163.
  notaryAppointmentDate: z.iso.date().nullable().default(null),
  status: ProspectStatusSchema,
  actor: ActorSchema,
  origin: ActivitySourceSchema.default('APP'),
  trigger: z.enum(PROSPECT_TRIGGERS).nullable().default(null),
  occurredAt: z.iso.datetime(),
});

export type ProspectPhaseChangedPayload = z.infer<typeof ProspectPhaseChangedPayloadSchema>;

export const ProspectStatusChangedPayloadSchema = z.object({
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  historyId: z.uuid(),
  fromStatus: ProspectStatusSchema,
  toStatus: ProspectStatusSchema,
  phase: ProspectPhaseSchema,
  lossReason: ProspectLossReasonSchema.nullable(),
  lossNote: z.string().nullable(),
  actor: ActorSchema,
  origin: ActivitySourceSchema.default('APP'),
  trigger: z.enum(PROSPECT_TRIGGERS).nullable().default(null),
  occurredAt: z.iso.datetime(),
});

export type ProspectStatusChangedPayload = z.infer<typeof ProspectStatusChangedPayloadSchema>;

export const ProspectOfferedPriceChangedPayloadSchema = z.object({
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  historyId: z.uuid(),
  previousOfferedPrice: z.number().nullable(),
  offeredPrice: z.number(),
  actor: ActorSchema,
  origin: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
});

export type ProspectOfferedPriceChangedPayload = z.infer<typeof ProspectOfferedPriceChangedPayloadSchema>;

export const ProspectOfferRecordedPayloadSchema = z.object({
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  offerId: z.uuid(),
  kind: ProspectOfferKindSchema,
  amount: z.number(),
  offeredOn: z.iso.date(),
  financingReservation: z.boolean(),
  actor: ActorSchema,
  origin: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
});

export type ProspectOfferRecordedPayload = z.infer<typeof ProspectOfferRecordedPayloadSchema>;

export const ProspectOfferStatusChangedPayloadSchema = z.object({
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  offerId: z.uuid(),
  // One per change: the Verlauf's content id, since one entry changes status many times.
  changeId: z.uuid(),
  kind: ProspectOfferKindSchema,
  amount: z.number(),
  fromStatus: ProspectOfferStatusSchema,
  toStatus: ProspectOfferStatusSchema,
  actor: ActorSchema,
  origin: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
});

export type ProspectOfferStatusChangedPayload = z.infer<typeof ProspectOfferStatusChangedPayloadSchema>;

export const ProspectNotaryAppointmentDateChangedPayloadSchema = z.object({
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  historyId: z.uuid(),
  previousNotaryAppointmentDate: z.iso.date().nullable(),
  notaryAppointmentDate: z.iso.date(),
  actor: ActorSchema,
  origin: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
});

export type ProspectNotaryAppointmentDateChangedPayload = z.infer<
  typeof ProspectNotaryAppointmentDateChangedPayloadSchema
>;

export const ProspectViewingAssessedPayloadSchema = z.object({
  assessmentId: z.uuid(),
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  appointmentId: z.uuid(),
  viewingStartsAt: z.iso.datetime(),
  viewingIsAllDay: z.boolean(),
  attendance: ProspectViewingAttendanceSchema,
  impression: ProspectViewingImpressionSchema.nullable(),
  comment: z.string().nullable(),
  firstRecordedAt: z.iso.datetime(),
  // 1 on the first save, then the row's version after each correction.
  revision: z.number().int().positive(),
  actor: ActorSchema,
  origin: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
});

export type ProspectViewingAssessedPayload = z.infer<typeof ProspectViewingAssessedPayloadSchema>;
